import { Injectable, inject } from '@angular/core'

import { EMPTY, Observable, catchError, from, map, of, switchMap } from 'rxjs'

import { ToastMessageService } from './toast-message.service'

export type ClipboardContext =
  'NODES' | 'RESOURCE_DATA_MODEL' | 'DATA_MODEL_DEPRECATED' | 'UI_ELEMENTS' | 'UI_ELEMENTS_STYLES'

@Injectable({ providedIn: 'root' })
export class ClipboardService {
  #localClipboard = {}
  #toastMessageService = inject(ToastMessageService)

  copyTo(key: ClipboardContext, data: string): Observable<void> {
    this.#localClipboard[key] = data
    return from(navigator.clipboard.readText()).pipe(
      switchMap((clipboardText) => {
        let resultData = { [key]: data }
        try {
          const clipboardData = JSON.parse(clipboardText)
          resultData = { ...clipboardData, [key]: data }
        } catch (e) {}
        return from(navigator.clipboard.writeText(JSON.stringify(resultData)))
      }),
      catchError((error) => {
        if (error instanceof TypeError) {
          this.#toastMessageService.showWarningMessage(
            'Clipboard API is not supported in your browser. Copy-paste will not work across different tabs.'
          )
        }
        return EMPTY
      })
    )
  }

  hasData(key: ClipboardContext): Observable<boolean> {
    return from(navigator.clipboard.readText()).pipe(
      map((clipboardText) => {
        try {
          const clipboardData = JSON.parse(clipboardText)
          if (clipboardData[key]) {
            return true
          } else {
            return false
          }
        } catch (e) {
          return false
        }
      }),
      catchError(() => of(!!this.#localClipboard[key]))
    )
  }

  pasteFrom(key: ClipboardContext): Observable<string> {
    return from(navigator.clipboard.readText()).pipe(
      map((clipboardText) => {
        try {
          const clipboardData = JSON.parse(clipboardText)
          if (clipboardData[key]) {
            return clipboardData[key]
          }
        } catch (e) {
          return clipboardText
        }
      }),
      catchError((error) => {
        if (error instanceof TypeError) {
          this.#toastMessageService.showWarningMessage(
            'Clipboard API is not supported in your browser. Copy-paste will not work across different tabs.'
          )
        }
        return of(this.#localClipboard[key] ?? null)
      })
    )
  }
}
